import { useCallback, useEffect, useRef, useState } from 'react';

import type { DoorVisitReportApi, DoorVisitReportRun } from './doorVisitReportApi';
import { getDoorVisitReportTerminalAnnouncement, isDoorVisitReportTerminal } from './doorVisitReportStatus';

export const DOOR_VISIT_REPORT_POLL_INTERVAL_MS = 5_000;

export type DoorVisitReportPollingScheduler = {
  setTimeout: (callback: () => void, delayMs: number) => ReturnType<typeof setTimeout>;
  clearTimeout: (timer: ReturnType<typeof setTimeout>) => void;
};

const defaultScheduler: DoorVisitReportPollingScheduler = {
  setTimeout: (callback, delayMs) => setTimeout(callback, delayMs),
  clearTimeout: timer => clearTimeout(timer),
};

export type DoorVisitReportVisibilityTarget = Pick<Document, 'visibilityState' | 'addEventListener' | 'removeEventListener'>;

const defaultVisibilityTarget = (): DoorVisitReportVisibilityTarget | null =>
  typeof document === 'undefined' ? null : document;

export type UseDoorVisitReportPollingOptions = {
  report: DoorVisitReportRun | null;
  api: Pick<DoorVisitReportApi, 'getReportRun'>;
  onReportChange: (report: DoorVisitReportRun) => void;
  onAutomaticError?: () => void;
  onTerminal?: (announcement: string, report: DoorVisitReportRun) => void;
  recoveryKey?: number;
  scheduler?: DoorVisitReportPollingScheduler;
  visibilityTarget?: DoorVisitReportVisibilityTarget | null;
};

export type UseDoorVisitReportPollingResult = {
  refreshing: boolean;
  refresh: () => void;
};

// One effect owns a report identity's timer, request, and visibility listener.
// It never schedules a second request until the prior request has settled.
export const useDoorVisitReportPolling = ({
  report,
  api,
  onReportChange,
  onAutomaticError,
  onTerminal,
  recoveryKey = 0,
  scheduler = defaultScheduler,
  visibilityTarget = defaultVisibilityTarget(),
}: UseDoorVisitReportPollingOptions): UseDoorVisitReportPollingResult => {
  const reportRef = useRef(report);
  const onReportChangeRef = useRef(onReportChange);
  const onAutomaticErrorRef = useRef(onAutomaticError);
  const onTerminalRef = useRef(onTerminal);
  const refreshRef = useRef<() => void>(() => undefined);
  const [refreshing, setRefreshing] = useState(false);

  reportRef.current = report;
  onReportChangeRef.current = onReportChange;
  onAutomaticErrorRef.current = onAutomaticError;
  onTerminalRef.current = onTerminal;

  const reportId = report?.id;

  useEffect(() => {
    if (reportId === undefined || report === null || isDoorVisitReportTerminal(report.status)) {
      refreshRef.current = () => undefined;
      setRefreshing(false);
      return undefined;
    }

    let active = true;
    let automaticPollingEnabled = true;
    let inFlight = false;
    let requestGeneration = 0;
    let timer: ReturnType<typeof setTimeout> | null = null;
    let controller: AbortController | null = null;
    let lastStatus = report.status;
    let lastAttempt = report.attempt;

    const isVisible = (): boolean => visibilityTarget?.visibilityState !== 'hidden';

    const clearTimer = (): void => {
      if (timer !== null) {
        scheduler.clearTimeout(timer);
        timer = null;
      }
    };

    const abortCurrentRequest = (): void => {
      requestGeneration += 1;
      const currentController = controller;
      controller = null;
      inFlight = false;
      currentController?.abort();
      if (active) setRefreshing(false);
    };

    const scheduleAutomaticRefresh = (): void => {
      if (!active || !automaticPollingEnabled || inFlight || !isVisible()) return;
      clearTimer();
      timer = scheduler.setTimeout(() => {
        timer = null;
        requestRefresh('automatic');
      }, DOOR_VISIT_REPORT_POLL_INTERVAL_MS);
    };

    const requestRefresh = (kind: 'automatic' | 'manual'): void => {
      if (!active || inFlight || reportRef.current?.id !== reportId) return;
      if (kind === 'automatic' && (!automaticPollingEnabled || !isVisible())) return;

      clearTimer();
      const requestController = new AbortController();
      const generation = ++requestGeneration;
      controller = requestController;
      inFlight = true;
      setRefreshing(true);

      void api.getReportRun(reportId, { signal: requestController.signal }).then(nextReport => {
        if (!active || requestController.signal.aborted || generation !== requestGeneration || reportRef.current?.id !== reportId) return;

        controller = null;
        inFlight = false;
        setRefreshing(false);
        const wasTerminal = isDoorVisitReportTerminal(lastStatus);
        const terminalAnnouncement = getDoorVisitReportTerminalAnnouncement(nextReport);
        const isNewTerminal =
          terminalAnnouncement !== null &&
          (!wasTerminal || lastAttempt !== nextReport.attempt || lastStatus !== nextReport.status);

        lastStatus = nextReport.status;
        lastAttempt = nextReport.attempt;
        onReportChangeRef.current(nextReport);

        if (isDoorVisitReportTerminal(nextReport.status)) {
          automaticPollingEnabled = false;
          if (isNewTerminal && terminalAnnouncement !== null) onTerminalRef.current?.(terminalAnnouncement, nextReport);
          return;
        }

        if (kind === 'manual') automaticPollingEnabled = true;
        scheduleAutomaticRefresh();
      }).catch(() => {
        if (!active || requestController.signal.aborted || generation !== requestGeneration || reportRef.current?.id !== reportId) return;

        controller = null;
        inFlight = false;
        setRefreshing(false);
        if (kind === 'automatic') {
          automaticPollingEnabled = false;
          onAutomaticErrorRef.current?.();
        }
      });
    };

    const handleVisibilityChange = (): void => {
      if (!isVisible()) {
        clearTimer();
        abortCurrentRequest();
        return;
      }

      if (automaticPollingEnabled && !inFlight) requestRefresh('automatic');
    };

    refreshRef.current = (): void => requestRefresh('manual');
    visibilityTarget?.addEventListener('visibilitychange', handleVisibilityChange);
    scheduleAutomaticRefresh();

    return () => {
      active = false;
      refreshRef.current = () => undefined;
      clearTimer();
      visibilityTarget?.removeEventListener('visibilitychange', handleVisibilityChange);
      abortCurrentRequest();
    };
  }, [api, recoveryKey, report, reportId, scheduler, visibilityTarget]);

  return { refreshing, refresh: useCallback(() => refreshRef.current(), []) };
};
